import React, { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { Instagram } from "lucide-react";
import { Navbar } from "../components/Navbar";
import { Footer } from "../components/Footer";
import { EventCard } from "../components/EventCard";
import { EventDetailModal } from "../components/EventDetailModal";
import { api } from "../lib/api";
import { CATEGORIES, LINKS } from "../lib/constants";
import { isPast } from "../lib/date";

export default function Events() {
  const [events, setEvents] = useState([]);
  const [filter, setFilter] = useState("All");
  const [active, setActive] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/events").then((r) => setEvents(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const { upcoming, past } = useMemo(() => {
    const filtered = events.filter((e) => filter === "All" || e.category === filter);
    const up = filtered.filter((e) => !isPast(e.end || e.start)).sort((a, b) => a.start.localeCompare(b.start));
    const pa = filtered.filter((e) => isPast(e.end || e.start)).sort((a, b) => b.start.localeCompare(a.start));
    return { upcoming: up, past: pa };
  }, [events, filter]);

  const empty = !loading && upcoming.length === 0 && past.length === 0;

  return (
    <div className="bg-paper min-h-screen" data-testid="events-page">
      <Navbar />

      <header className="max-w-7xl mx-auto px-6 md:px-12 pt-32 md:pt-44 pb-12">
        <h1 className="font-serif text-6xl md:text-7xl font-black tracking-tight text-ink leading-none">
          What's next.
        </h1>
        <p className="font-sans text-lg md:text-xl text-ink/70 max-w-2xl mt-6 leading-relaxed">
          Show up, learn the dance, give back, or find your next opportunity.
        </p>
      </header>

      {/* Filter tabs */}
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex gap-1 md:gap-3 border-b border-ink/10 overflow-x-auto no-scrollbar" role="tablist">
          {CATEGORIES.map((cat) => (
            <button
              key={cat}
              role="tab"
              aria-selected={filter === cat}
              onClick={() => setFilter(cat)}
              data-testid={`filter-${cat.toLowerCase()}`}
              className={`font-sans text-sm md:text-base px-4 py-3 whitespace-nowrap transition-colors -mb-px border-b-2 ${
                filter === cat
                  ? "text-trailride border-trailride font-semibold"
                  : "text-ink/50 border-transparent hover:text-ink"
              }`}
            >
              {cat}
            </button>
          ))}
        </div>
      </div>

      <section className="max-w-7xl mx-auto px-6 md:px-12 py-14 md:py-20">
        {empty && (
          <div className="py-24 text-center" data-testid="events-empty">
            <p className="font-serif text-3xl md:text-4xl text-ink mb-4">More is on the way.</p>
            <p className="font-sans text-ink/60 mb-8">Follow us on Instagram for the latest.</p>
            <a
              href={LINKS.instagram}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-8 py-4 rounded-full bg-trailride text-white font-sans font-medium hover:bg-trailride-dark transition-colors"
            >
              <Instagram size={18} /> Follow @tigertrailride
            </a>
          </div>
        )}

        {upcoming.length > 0 && (
          <div className="mb-20" data-testid="upcoming-events">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
              {upcoming.map((event, i) => (
                <motion.div
                  key={event.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-60px" }}
                  transition={{ duration: 0.6, delay: (i % 3) * 0.08 }}
                >
                  <EventCard event={event} onOpen={setActive} />
                </motion.div>
              ))}
            </div>
          </div>
        )}

        {past.length > 0 && (
          <div data-testid="past-events">
            <p className="font-sans text-xs tracking-[0.24em] uppercase text-ink/40 mb-8 border-t border-ink/10 pt-10">
              Archive · Past events
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
              {past.map((event) => (
                <EventCard key={event.id} event={event} onOpen={setActive} past />
              ))}
            </div>
          </div>
        )}
      </section>

      <Footer />
      <EventDetailModal event={active} open={!!active} onClose={() => setActive(null)} />
    </div>
  );
}
